import { ChangeEvent, PropsWithChildren } from "react"
import { Form, Formik, FormikHelpers, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import * as yup from "yup"
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers"
import { DialogContent, Grid, InputAdornment, MenuItem, TextField, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../../currency/currencyLib"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { FormikDropdown } from "../../../../../../shared/components/forms/Formik"
import { useDateTime } from "../../../../../localization/useDateTime"
import { CurrencyCode } from "../../../priceList/priceListTypes"
import { iterateStringEnum } from "../../../../../../lib/enums"
import { NumberFormatCustom } from "../FormikCurrencyField"
import { MarketingTags } from "./MarketingTags"
import {
    CreateUpdateProductFormValues,
    DiscountType,
    OfferDetailsFormValues,
    OfferDetailsType,
} from "../../productsTypes"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    dialogPaper: {
        padding: "1rem",
        justifyContent: "center",
        maxWidth: "73rem",
    },
    dialogTitle: {
        textAlign: "center",
        fontSize: "2.5rem",
        fontWeight: 600,
        paddingTop: "2rem",
        color: theme.palette.tailwind.slate[700],
    },
    content: {
        width: "100%",
        padding: "0 4rem",
    },
    box: {
        width: "100%",
        boxShadow: "0px 2px 12px 0px #0000001F",
        borderRadius: "0.5rem",
    },
    boxUpdate: {
        gap: "2.25rem",
        padding: "2rem",
    },
    boxTitle: {
        padding: "1.25rem",
        color: theme.palette.tailwind.slate[600],
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    inputUi: {
        width: "100%",
        maxWidth: "12.5rem",
    },
}))

type OfferDialogProps = Readonly<
    PropsWithChildren<{
        open: boolean
        onClose: () => void
        initialValues?: OfferDetailsFormValues
        handleSaveOffer: (data: OfferDetailsType) => void
    }>
>

export function OfferDialog({ open, onClose, initialValues, handleSaveOffer }: OfferDialogProps) {
    const { values } = useFormikContext<CreateUpdateProductFormValues>()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const defaultInitialValues = {
        discount: {
            type: DiscountType.NONE,
        },
        availabilityDetails: {},
    }

    const validationSchema = yup.object({
        discount: yup.object({
            type: yup.string().required("Type is required"),
            percentage: yup
                .object()
                .when("type", {
                    is: (type: DiscountType) => type === DiscountType.DISCOUNT_PERCENTAGE,
                    then: () =>
                        yup.object({
                            amount: yup.string().required(l10n.getString("required-field")).min(1),
                        }),
                })
                .when("type", {
                    is: (type: DiscountType) => type !== DiscountType.DISCOUNT_PERCENTAGE,
                    then: () =>
                        yup.object({
                            amount: yup.string().notRequired(),
                        }),
                }),
            price: yup.number().when("type", {
                is: (type: DiscountType) => type === DiscountType.DISCOUNT_PRICE,
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        productsOnSale: yup.number(),
        availabilityDetails: yup.object({
            dateTimeInterval: yup.object({
                from: yup.date(),
                to: yup.date(),
            }),
        }),
    })

    function handleSubmit(date: OfferDetailsFormValues, _helpers: FormikHelpers<OfferDetailsFormValues>) {
        {
            const { discount, availabilityDetails, productsOnSale } = date
            const { percentage, price, type, displayLabelType } = discount
            const dateTimeIntervalFrom = availabilityDetails.dateTimeInterval?.from
                ? dateTimeFactory.toApi(availabilityDetails.dateTimeInterval.from)
                : undefined
            const dateTimeIntervalTo = availabilityDetails.dateTimeInterval?.to
                ? dateTimeFactory.toApi(availabilityDetails.dateTimeInterval.to)
                : undefined

            const availabilityDetailsValue = {
                dateTimeInterval: {
                    ...(dateTimeIntervalFrom !== undefined && { from: dateTimeIntervalFrom }),
                    ...(dateTimeIntervalTo !== undefined && { to: dateTimeIntervalTo }),
                },
            }
            const scaleUp = (scaledAmount: number, scale: number) => {
                return scaledAmount * Math.pow(10, scale)
            }

            const modifyData = {
                ...(productsOnSale && { productsOnSale: productsOnSale }),
                availabilityDetails: availabilityDetailsValue,
                discount: {
                    type,
                    ...(discount.type === DiscountType.DISCOUNT_PERCENTAGE
                        ? {
                              percentage: {
                                  amount: scaleUp(Number(percentage?.amount), 1),
                                  scale: 1,
                              },
                          }
                        : {}),
                    ...(discount.type === DiscountType.DISCOUNT_PRICE
                        ? {
                              price: toMonetaryAmount(toDineroLossy(price!, toCurrency(values.currency))),
                          }
                        : {}),
                    ...(discount.type !== DiscountType.NONE ? { displayLabelType } : {}),
                },
            }
            handleSaveOffer(modifyData)
            onClose()
        }
    }

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            dialogTitle={
                initialValues ? (
                    <LocalizedStrict id="product-create-offer-dialog-edit-title">
                        <Typography className={classes.dialogTitle}>Edit offer</Typography>
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict id="product-create-offer-dialog-create-title">
                        <Typography className={classes.dialogTitle}>Create offer</Typography>
                    </LocalizedStrict>
                )
            }
            slotProps={{ paper: { className: classes.dialogPaper } }}
            scroll="body"
        >
            <Grid container justifyContent="center" alignItems="center" direction="column" wrap="nowrap">
                <Grid container flexDirection="column" gap={5} className={classes.content}>
                    <Grid
                        container
                        alignItems="flex-end"
                        justifyContent="space-between"
                        flexWrap="nowrap"
                        gap={3}
                        padding="0 2.5rem"
                    >
                        <Typography variant="h4" className={classes.title}>
                            {values.name ?? "-"}
                        </Typography>
                        <Grid container width="auto" flexDirection="column" alignItems="flex-end">
                            <LocalizedStrict id="product-create-offer-dialog-normal-price">
                                <Typography>Normal price:</Typography>
                            </LocalizedStrict>
                            <Typography variant="h4">
                                <Typography variant="inherit" fontWeight={200} display="inline">
                                    {values.currency ?? ""}
                                </Typography>{" "}
                                {values.price ?? ""}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Formik<OfferDetailsFormValues>
                        enableReinitialize
                        initialValues={initialValues || defaultInitialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnBlur={false}
                        validateOnChange={false}
                    >
                        <Form>
                            <OfferDetailsFormContent />
                        </Form>
                    </Formik>
                </Grid>
            </Grid>
        </StandardDialog>
    )
}

export function OfferDetailsFormContent() {
    const { classes, cx } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<OfferDetailsFormValues>()
    const { discount, availabilityDetails } = values

    const handleNumericChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target
        const onlyNumbs = value.replace(/[^0-9]/g, "")
        return onlyNumbs
    }

    return (
        <DialogContent>
            <Grid
                container
                flexWrap="nowrap"
                justifyContent="space-between"
                alignItems="flex-start"
                marginBottom="2.5rem"
            >
                <Grid
                    container
                    xs={7}
                    flexDirection="column"
                    alignItems="flex-start"
                    className={cx(classes.box, classes.boxUpdate)}
                >
                    <Grid maxWidth="20rem" width="100%">
                        <LocalizedStrict
                            id="product-create-offer-dialog-price-adjustment-field"
                            attrs={{ label: true }}
                        >
                            <FormikDropdown name="discount.type" label="Price adjustment" fullWidth>
                                {[...iterateStringEnum(DiscountType)].map((type) => (
                                    <MenuItem key={type} value={type}>
                                        <LocalizedEnum
                                            base="product-create-offer-dialog-price-adjustment-option"
                                            enumValue={type}
                                        >
                                            <span>{type}</span>
                                        </LocalizedEnum>
                                    </MenuItem>
                                ))}
                            </FormikDropdown>
                        </LocalizedStrict>
                    </Grid>
                    {discount.type === DiscountType.DISCOUNT_PERCENTAGE && (
                        <LocalizedStrict id="product-create-offer-dialog-percentage-field" attrs={{ label: true }}>
                            <TextField
                                label="Percentage"
                                name="discount.percentage.amount"
                                value={discount?.percentage?.amount}
                                onChange={(event) => {
                                    const percentageValue = handleNumericChange(event)
                                    const percentage = Number(percentageValue) >= 100 ? "100" : percentageValue
                                    setFieldValue("discount.percentage.amount", percentage)
                                }}
                                inputProps={{
                                    style: { textAlign: "right" },
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                error={Boolean(errors.discount?.percentage)}
                                className={classes.inputUi}
                            />
                        </LocalizedStrict>
                    )}
                    {discount.type === DiscountType.DISCOUNT_PRICE && (
                        <LocalizedStrict id="product-create-offer-dialog-adjust-price-field" attrs={{ label: true }}>
                            <TextField
                                label="Price"
                                value={discount.price}
                                onChange={(e) => {
                                    setFieldValue("discount.price", e.target.value)
                                }}
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                    startAdornment: (
                                        <InputAdornment position="start">{CurrencyCode.DKK}</InputAdornment>
                                    ),
                                }}
                                error={Boolean(errors.discount?.price)}
                                helperText={errors.discount?.price}
                                className={classes.inputUi}
                            />
                        </LocalizedStrict>
                    )}

                    <LocalizedStrict id="product-create-offer-dialog-items-field" attrs={{ label: true }}>
                        <TextField
                            type="text"
                            name="productsOnSale"
                            value={values.productsOnSale}
                            onChange={(event) => {
                                const productsOnSale = handleNumericChange(event)
                                setFieldValue(
                                    "productsOnSale",
                                    productsOnSale === "" ? undefined : Number(productsOnSale),
                                )
                            }}
                            label="Available items per day"
                            error={Boolean(errors.productsOnSale)}
                            helperText={errors.productsOnSale}
                            className={classes.inputUi}
                        />
                    </LocalizedStrict>

                    {discount.type !== DiscountType.NONE && <MarketingTags />}
                </Grid>
                <Grid item xs={4} className={classes.box}>
                    <LocalizedStrict id="product-create-offer-dialog-available-offer-period">
                        <Typography variant="subtitle1" className={classes.boxTitle}>
                            Offer period
                        </Typography>
                    </LocalizedStrict>
                    <Grid container flexDirection="column" gap={4.5} padding="2rem 1.25rem">
                        <LocalizedStrict
                            id="product-create-offer-dialog-available-from-date-picker"
                            attrs={{ label: true }}
                        >
                            <DateTimePicker
                                value={availabilityDetails.dateTimeInterval?.from}
                                onChange={(newValue) => {
                                    setFieldValue("availabilityDetails.dateTimeInterval.from", newValue)
                                }}
                                label="Available from"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                }}
                                format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                                maxDateTime={availabilityDetails.dateTimeInterval?.to}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="product-create-offer-dialog-available-to-date-picker"
                            attrs={{ label: true }}
                        >
                            <DateTimePicker
                                value={availabilityDetails.dateTimeInterval?.to}
                                onChange={(newValue) => {
                                    setFieldValue("availabilityDetails.dateTimeInterval.to", newValue)
                                }}
                                label="Available to"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                }}
                                format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                                minDateTime={availabilityDetails.dateTimeInterval?.from}
                            />
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justifyContent="center">
                <LocalizedStrict id="product-create-offer-dialog-save-button">
                    <StandardButton fullWidth={false} type="submit">
                        Save
                    </StandardButton>
                </LocalizedStrict>
            </Grid>
        </DialogContent>
    )
}
